import {
  LayoutDashboard,
  Users,
  UserCog,
  UserCircle,
  Building2,
  Building,
  Layers,
  ShieldCheck,
  Megaphone,
  FileText,
  FileArchive,
  FileSpreadsheet,
  FileBox,
  FileBadge,
  IdCard,
  Layers3,
  CalendarDays,
  CalendarClock,
  Sheet,
  Settings,
  Cog,
  Lock,
  Briefcase,
  UserCheck,
  UserPlus,
  ClipboardList,
  Wallet,
  DollarSign,
  Receipt,
  HandCoins,
  MinusCircle,
  Coins,
  PlaneTakeoff,
  UserX,
  Inbox,
  FileSignature,
  GraduationCap,
  Calculator,
  Clock,
  Clipboard,
  Package,
  Boxes,
  Monitor,
  HardDrive,
  Server,
  BarChart3,
  LifeBuoy,
  Database,
  ArrowLeftRight,
  CreditCard,
  Target,
  ListChecks,
  TrendingUp,
  CheckCircle2,
  Plug,
  Sparkles,
  ShieldOff,
  AlertTriangle,
  Award,
  Scale,
  MessageSquareWarning,
  MessageSquare,
  ListTree,
  MapPin,
  DoorOpen,
  Grid3X3,
} from "lucide-react";
import { useEffect, useState } from "react";
import { buildDynamicNav, patchMasterBlockFloorPermissions } from "../components/common/CommonFunctions";
import { buildNavDefaults } from "../config/navPermissions";
import { isOrgOverview, BRANCH_FILTER_CHANGED_EVENT } from "../utils/branchSession";
import { filterNavByPlanModules, getSessionPlanModules } from "../utils/planModules";
import { isOvertimeRequestWorkflowEnabled } from "../utils/customConfigSession";
import { getMasterMigrationNavEntries, MASTER_MIGRATION_DEFAULT_LABELS } from "../pages/migrations/masterMigrationConfigs";

// Custom event name dispatched by pages (Home, AdminDashboard) after they
// hydrate sessionStorage with customConfig/permissions so the sidebar can
// re-render without requiring a hard page refresh. Exported for callers.
export const SESSION_STORAGE_HYDRATED_EVENT = "hrms:session-storage-hydrated";

export const useNavigation = () => {

  // Force re-evaluation of the synchronous sessionStorage reads below when
  // another component finishes hydrating session storage and dispatches the
  // event. Without this, the sidebar mounts before the dashboard fetch
  // completes and ends up missing flag-gated items like Training.
  const [, setHydrationTick] = useState(0);
  useEffect(() => {
    const onHydrated = () => setHydrationTick((n) => n + 1);
    const onBranchScopeChange = () => setHydrationTick((n) => n + 1);
    window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, onHydrated);
    // Header branch dropdown changes mode/filter in sessionStorage; rebuild
    // the sidebar immediately (overview vs full menu) without a remount.
    window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchScopeChange);
    window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onHydrated);
    // Cross-tab safety net — keep the sidebar in sync if customConfig changes
    // in another tab.
    window.addEventListener("storage", onHydrated);
    return () => {
      window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, onHydrated);
      window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchScopeChange);
      window.removeEventListener("storage", onHydrated);
    };
  }, []);

  const userPermissionList = patchMasterBlockFloorPermissions(
    JSON.parse(sessionStorage.getItem("loginUserPermission")) || []
  );
  const is_superAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false")
  const is_Admin = JSON.parse(sessionStorage.getItem("is_admin") || "false")
  const customConfig = JSON.parse(sessionStorage.getItem("customConfig")) || {};
  const isTrainingEnabled = !!customConfig.is_nabh;
  // Branch Transfer is only meaningful when the company has more than one active
  // branch, and is restricted to ORG ADMIN and BRANCH MANAGER (branch admin)
  // only — a plain employee never raises or approves transfers. multi_branch is
  // role-independent (2+ branches); the role check is what hides it from staff.
  // Independent of the is_branch_enabled toggle (that gates branch CRUD only).
  const isBranchAdmin = JSON.parse(sessionStorage.getItem("is_branch_admin") || "false");
  const reportingManagerRaw = sessionStorage.getItem("reportingManager");
  const isReportingManager = reportingManagerRaw === "true" || reportingManagerRaw === true;
  const multiBranch = JSON.parse(sessionStorage.getItem("multi_branch") || "false");
  const showOvertimeRequest = isOvertimeRequestWorkflowEnabled();

  const defaults = buildNavDefaults({
    is_superAdmin,
    is_Admin,
    isBranchAdmin,
    isReportingManager,
    showOvertimeRequest,
    isTrainingEnabled,
    masterMigrationDefaultLabels: MASTER_MIGRATION_DEFAULT_LABELS,
  });

  const navItems = [
    // Dashboard changes based on role
    { label: "Home", href: "/home", icon: LayoutDashboard },

    // Admin Panel (only for admin roles)
    {
      label: "Administrator",
      href: "/admin",
      icon: UserCog,
      subItems: [
        { label: "Organization", href: "/admin/companyprofile", icon: Building2, permKey: "organization" },
        { label: "User Group", href: "/admin/usergroup", icon: Users, permKey: "user_group" },
        { label: "User Permission", href: "/admin/userpermission", icon: ShieldCheck, permKey: "user_permission" },
        { label: "Notice Board", href: "/admin/noticeboard", icon: Megaphone, permKey: "notice_board" },
        { label: "Password Policy", href: "/admin/passwordpolicy", icon: Lock, permKey: "password_policy" },
        { label: "Organization Documents", href: "/admin/organizationdocuments", icon: FileArchive, permKey: "organization_documents" },
      ],
    },

    // Master (Admin/Superadmin only)
    {
      label: "Master",
      href: "/master",
      icon: Layers3,
      subItems: [
        { label: "Employee Type", href: "/master/employeetype", icon: IdCard, permKey: "employee_type" },
        { label: "Department", href: "/master/department", icon: Building2, permKey: "department" },
        { label: "Designation", href: "/master/designation", icon: Briefcase, permKey: "designation" },
        { label: "City", href: "/master/city", icon: MapPin, permKey: "city" },
        { label: "Block", href: "/master/block", icon: Building, permKey: "block" },
        { label: "Floor", href: "/master/floor", icon: Layers, permKey: "floor" },
        { label: "Room", href: "/master/room", icon: DoorOpen, permKey: "room" },
        { label: "Leave Type", href: "/master/leavetype", icon: CalendarClock, permKey: "leave_type" },
        { label: "Employee Group", href: "/master/employeegroup", icon: Users, permKey: "employee_group" },
        { label: "Reimbursements Type", href: "/master/reimbursementstype", icon: Receipt, permKey: "reimbursements_type" },
        { label: "Asset Type", href: "/master/assettype", icon: Package, permKey: "asset_type" },
        { label: "Asset Category", href: "/master/assetcategory", icon: Boxes, permKey: "asset_category" },
        { label: "Asset Sub Category", href: "/master/assetsubategory", icon: FileBox, permKey: "asset_sub_category" },
        { label: "Letter Type", href: "/master/lettertype", icon: FileText, permKey: "letter_type" },
        { label: "Letter Template", href: "/master/lettertemplate", icon: FileSignature, permKey: "letter_template" },
        { label: "Document Type", href: "/master/documenttype", icon: FileBadge, permKey: "document_type" },
      ],
    },

    // Configuration (Admin/Superadmin only)
    {
      label: "Configuration",
      href: "/configuration/shiftconfiguration",
      icon: Settings,
      subItems: [
        { label: "Shift Configuration", href: "/configuration/shiftconfiguration", icon: CalendarClock, permKey: "shift_configuration" },
        { label: "Holiday", href: "/configuration/holiday", icon: CalendarDays, permKey: "holiday" },
        { label: "Week off", href: "/configuration/weekoff", icon: Clipboard, permKey: "week_off" },
        { label: "Payroll Period", href: "/configuration/payrollperiod", icon: CalendarDays, permKey: "payroll_period" },
        { label: "Subordinate", href: "/configuration/subordinate", icon: UserCheck, permKey: "subordinate" },
        { label: "Salary Component", href: "/configuration/salarycomponent", icon: Wallet, permKey: "salary_component" },
        { label: "Salary Template", href: "/configuration/salarytemplate", icon: FileSpreadsheet, permKey: "salary_template" },
        { label: "DA Revision", href: "/configuration/darevision", icon: TrendingUp, permKey: "da_revision" },
      ],
    },

    // Employee Section
    {
      label: "Employee",
      href: "/employee",
      icon: UserCircle,
      subItems: [
        { label: "Employees", href: "/employee", icon: Users, permKey: "employees" },
        { label: "Occupancy", href: "/employee/occupancy", icon: Building, permKey: "employees" },
        multiBranch ? { label: "Branch Transfer", href: "/employee/branch-transfer", icon: ArrowLeftRight, permKey: "branch_transfer" } : null,
        { label: "Transfer Reporting Manager", href: "/employee/transfer-reporting-manager", icon: UserCheck, permKey: "transfer_reporting_manager" },
        { label: "Leave Request", href: "/employee/leaverequest", icon: PlaneTakeoff, permKey: "leave_request" },
        { label: "Reimbursement Request", href: "/employee/reimbursementrequest", icon: Receipt, permKey: "reimbursements_request" },
        { label: "Advance Salary Request", href: "/employee/advancerequest", icon: HandCoins, permKey: "advance_salary_request" },
        showOvertimeRequest ? { label: "Overtime Request", href: "/employee/overtimerequest", icon: Clock, permKey: "overtime_request" } : null,
        { label: "Subordinates", href: "/employee/subordinate", icon: UserCheck, permKey: "subordinates" },
        { label: "Employee Allowances", href: "/employee/allowances", icon: Coins, permKey: "employee_allowances" },
        { label: "Employee Deductions", href: "/employee/deductions", icon: MinusCircle, permKey: "employee_deductions" },
        { label: "Employee Deposits", href: "/employee/deposits", icon: Wallet, permKey: "employee_deposits" },
        { label: "Resignation", href: "/employee/resignationrequest", icon: UserX, permKey: "resignation" },
        { label: "Abscond", href: "/employee/absconding", icon: AlertTriangle, permKey: "absconding" },
        { label: "Approval Inbox", href: "/employee/approvalinbox", icon: Inbox, permKey: "approval_inbox" },
        { label: "My Tasks", href: "/employee/timesheet", icon: ClipboardList, permKey: "timesheet" },
        { label: "Team Tasks", href: "/employee/team/timesheet", icon: ClipboardList, permKey: "team_timesheet" },
        { label: "Employee Onboarding", href: "/employee/onboarding", icon: UserPlus, permKey: "employee_onboarding" },
        { label: "Intern Onboarding", href: "/employee/internonboarding", icon: GraduationCap, permKey: "intern_onboarding" },
      ].filter(Boolean),
    },

    // Payroll (Admin/Superadmin only)
    {
      label: "Payroll",
      href: "/payroll",
      icon: DollarSign,
      subItems: [
        { label: "Payroll Dashboard", href: "/payroll/dashboard", icon: BarChart3, permKey: "payroll_dashboard" },
        { label: "Payroll", href: "/payroll/payrollprocess", icon: FileText, permKey: "payroll" },
        { label: "Payslips", href: "/payroll/payslips", icon: FileSpreadsheet, permKey: "payslips" },
        { label: "TDS Preview", href: "/payroll/tdspreview", icon: Receipt, permKey: "tds_preview" },
        { label: "TDS Configuration", href: "/payroll/tdsconfiguration", icon: Calculator, permKey: "tds_configuration" },
        { label: "Payroll Hold", href: "/payroll/payrollhold", icon: ShieldOff, permKey: "payroll_hold" },
        { label: "Attendance Penalties", href: "/payroll/attendancepenalties", icon: AlertTriangle, permKey: "attendance_penalties" },
        { label: "Perk Criteria", href: "/payroll/perkcriteria", icon: Award, permKey: "perk_criteria" },
      ],
    },

    // Attendance (All users, but different)
    {
      label: "Attendance",
      href: "/attendance",
      icon: Clock,
      subItems: [
        { label: "Attendance Dashboard", href: "/attendance", icon: BarChart3, permKey: "attendance_dashboard" },
        { label: "Day Attendance", href: "/attendance/dayattendance", icon: CalendarDays, permKey: "day_wise_attendance" },
        { label: "Attendance Log", href: "/attendance/attendancelog", icon: ClipboardList, permKey: "attendance_log" },
        {
          label: "My Shift Change / Swap",
          href: "/attendance/my-shift-change-requests",
          icon: ArrowLeftRight,
          permKey: "shift_change_requests",
        },
        ...(is_superAdmin ? [{
          label: "Shift Change / Swap Approvals",
          href: "/attendance/shift-change-requests",
          icon: ArrowLeftRight,
          permKey: "shift_change_requests",
        }] : []),
        {
          label: "Punch Discrepancies",
          href: "/attendance/punch-discrepancies",
          icon: AlertTriangle,
          permKey: "punch_discrepancies",
        },
        {
          label: "Penalty Appeals",
          href: "/attendance/penalty-appeals",
          icon: Scale,
          permKey: "penalty_appeals",
        },
      ],
    },

    {
      label: "Asset Management",
      href: "/assetmanagement",
      icon: Monitor,
      subItems: [
        { label: "Asset Request", href: "/assetmanagement/assetrequest", icon: Inbox, permKey: "asset_request" },
        { label: "Asset Registry", href: "/assetmanagement/assetregistry", icon: HardDrive, permKey: "asset_registry" },
        { label: "Asset Employee Audit", href: "/assetmanagement/assetemployeeaudit", icon: Server, permKey: "asset_employee_audit" },
      ],
    },

    // Roster — shift changes live under Attendance; roster report lives under Reports.
    {
      label: "Roster",
      href: "/roster",
      icon: CalendarClock,
      subItems: [
        { label: "Published Rosters", href: "/roster", icon: CalendarClock, permKey: "roster" },
        { label: "Duty Roster", href: "/roster/duty-roster", icon: Sheet, permKey: "duty_roster" },
      ],
    },

    // Appraisal — permission keys align with AppraisalModule / appraisal/urls.py.
    {
      label: "Appraisal",
      href: "/appraisal",
      icon: Target,
      subItems: [
        { label: "Dashboard", href: "/appraisal", icon: LayoutDashboard, permKey: "my_appraisals" },
        { label: "My Appraisal", href: "/appraisal/my-appraisal", icon: ClipboardList, permKey: "my_appraisals" },
        { label: "Team Review", href: "/appraisal/team-review", icon: UserCheck, permKey: "team_appraisals" },
        { label: "Feedback Requests", href: "/appraisal/feedback-requests", icon: MessageSquare, permKey: "appraisal_feedback" },
        { label: "KPI Manager", href: "/appraisal/kpi-manager", icon: ListChecks, permKey: "appraisal_kpis" },
        { label: "Cycles", href: "/appraisal/cycles", icon: CalendarClock, permKey: "appraisal_cycles" },
        { label: "Final Approval", href: "/appraisal/final-approval", icon: CheckCircle2, permKey: "appraisal_approval" },
        { label: "Calibration", href: "/appraisal/calibration", icon: Scale, permKey: "appraisal_approval" },
        { label: "Nine-Box", href: "/appraisal/nine-box", icon: Grid3X3, permKey: "team_appraisals" },
        { label: "Competencies", href: "/appraisal/competencies", icon: Sparkles, permKey: "appraisal_kpis" },
      ],
    },


    // Grievance. Sub-items are listed unconditionally on purpose: buildDynamicNav
    // shows every sub-item to an admin, and filters the rest against the user
    // group's own permission rows. Role-gating them here instead would hide the
    // inbox from the non-admin HR staff the `grievance_inbox` permission exists
    // to serve — their API calls would work while the menu stayed empty, which
    // is the hardest version of this bug to diagnose.
    // "My Grievances" is in `defaults` below so every employee keeps it without
    // an explicit permission row; the other three are permission-gated.
    {
      label: "Grievance",
      href: "/grievance/my-grievances",
      icon: MessageSquareWarning,
      subItems: [
        { label: "My Grievances", href: "/grievance/my-grievances", icon: MessageSquare, permKey: "my_grievances" },
        { label: "Grievance Inbox", href: "/grievance/inbox", icon: Inbox, permKey: "grievance_inbox" },
        { label: "Grievance Categories", href: "/grievance/categories", icon: ListTree, permKey: "grievance_categories" },
        { label: "Grievance Dashboard", href: "/grievance", icon: BarChart3, permKey: "grievance_dashboard" },
      ],
    },

    {
      label: "Internship",
      href: "/internship/programs",
      icon: Layers,
      subItems: [
        { label: "Programs", href: "/internship/programs", icon: Layers, permKey: "internship_programs" },
        { label: "Enrolments", href: "/internship/enrolments", icon: UserPlus, permKey: "internship_enrolments" },
        { label: "My Internship", href: "/internship/mine", icon: GraduationCap, permKey: "my_internship" },
      ],
    },

    isTrainingEnabled && {
      label: "Training",
      href: "/training",
      icon: GraduationCap,
      subItems: (is_superAdmin)
        ? [
          { label: "All Trainings", href: "/training", icon: ClipboardList, permKey: "assign_training" },
          { label: "Assign Training", href: "/training/add", icon: UserPlus, permKey: "assign_training" },
          { label: "Overdue", href: "/training/overdue", icon: CalendarClock, permKey: "assign_training" },
        ]
        : [
          { label: "My Trainings", href: "/training", icon: ClipboardList, permKey: "my_trainings" },
          { label: "Team Trainings", href: "/training/team", icon: UserCheck, permKey: "team_trainings" },
          { label: "Assign Training", href: "/training/add", icon: UserPlus, permKey: "assign_training" },
        ],
    },

    {
      label: "Data Migration",
      href: "/datamigration/employeemigration",
      icon: Database,
      subItems: [
        { label: "Employee Migration", href: "/datamigration/employeemigration", icon: ArrowLeftRight },
      ],
    },

    // Reports (all, but richer for admin)
    {
      label: "Reports",
      href: "/reports",
      icon: BarChart3,
      isSecondary: true,
    },

    // Settings (admin only)
    {
      label: "Settings",
      href: "/settings",
      icon: Settings,
      isSecondary: true,
    },

    // eMedHub Integration — org-admin utility; page prompts setup when not linked.
    {
      label: "Integration",
      href: "/integration",
      icon: Plug,
      isSecondary: true,
    },

    // { label: "Plans", href: "/plans", icon: CreditCard, isSecondary: true },

    // Help (shown only when granted via fullMenus — admin / branch admin)
    { label: "IT Help Desk", href: "/ithelpdesk", icon: LifeBuoy, isSecondary: true },

    // AI Copilot — same as IT Help Desk (admin / branch admin defaults only).
    { label: "AI Copilot", href: "/ai/copilot", icon: Sparkles, isSecondary: true },


  ].filter(Boolean); // remove false entries

  const masterMigrationNavItems = getMasterMigrationNavEntries().map((entry) => ({
    label: entry.label,
    href: entry.href,
    icon: FileSpreadsheet,
    permKey: "master migration",
  }));

  // Inject master migration links into Data Migration (below Employee Migration).
  const navItemsWithMasterMigration = navItems.map((item) => {
    if (item.label !== "Data Migration") return item;
    return {
      ...item,
      subItems: [
        { label: "Employee Migration", href: "/datamigration/employeemigration", icon: ArrowLeftRight, permKey: "employee migration" },
        ...masterMigrationNavItems,
      ],
    };
  });

  const planModules = getSessionPlanModules();
  const planFilteredNavItems = filterNavByPlanModules(navItemsWithMasterMigration, planModules);
  const dynamicNav = buildDynamicNav(planFilteredNavItems, userPermissionList, defaults);
  // const dynamicNav = navItems

  // Org admin in Dashboard Overview (all-branches) mode: Dashboard, Employee,
  // Reports and Settings. Every Master table is branch-owned (same as
  // Department), so Master stays on the full menu inside a named branch.
  // Administrator, Configuration, Integration and Data Migration stay hidden.
  if (isOrgOverview()) {
    const overviewItems = [
      { label: "Dashboard", href: "/home", icon: LayoutDashboard },
      {
        label: "Employee",
        href: "/employee",
        icon: UserCircle,
        subItems: [
          { label: "Employees", href: "/employee", icon: Users },
          { label: "Occupancy", href: "/employee/occupancy", icon: Building },
          { label: "Branch Transfer", href: "/employee/branch-transfer", icon: ArrowLeftRight },
        ],
      },
      { label: "Reports", href: "/reports", icon: BarChart3, isSecondary: true },
      { label: "Settings", href: "/settings", icon: Settings, isSecondary: true },
    ].filter(Boolean);
    const overviewNav = buildDynamicNav(
      filterNavByPlanModules(overviewItems, planModules),
      userPermissionList,
      defaults,
    );
    return { navItems: planFilteredNavItems, dynamicNav: overviewNav };
  }

  return { navItems: planFilteredNavItems, dynamicNav };
};
